import React, { useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import '../../styles/auth.css';
import logoPng from '../../assets/images/logo.png';
import http from '../../lib/http';
import { toast } from 'react-toastify';

const LockIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const ArrowIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

const BackIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12" />
    <polyline points="12 19 5 12 12 5" />
  </svg>
);

const ShieldIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <polyline points="9 12 11 14 15 10" />
  </svg>
);

export default function ResetPassword() {
  const location = useLocation();
  const navigate = useNavigate();

  // Comes from EmailVerification's navigate('/reset-password', { state: { email } })
  // after a successful forgot-password/verify-otp call.
  const email = location.state?.email || '';

  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email) {
      setError('Session expired. Please restart the password reset process.');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    if (password !== passwordConfirmation) {
      setError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      // Backend expects: email, password, password confirmed via Laravel's
      // 'confirmed' rule -> requires a `password_confirmation` field.
      await http.post('/api/customer/forgot-password/reset', {
        email,
        password,
        password_confirmation: passwordConfirmation,
      });

      toast.success('Password reset successfully. Please log in.');
      navigate('/login', { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.message || 'Could not reset password. Please try again.';
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-left">
        <div className="auth-left-content">
          <div className="auth-left-logo auth-left-logo-xl">
            <img src={logoPng} alt="Easy World Online" />
          </div>

          <h1 className="auth-left-title">
            Set a new<br />password.
          </h1>

          <p className="auth-left-sub">
            Choose a strong new password to secure your account.
          </p>

          <div className="auth-left-pills">
            <span className="auth-pill">🔒 Secure Reset</span>
            <span className="auth-pill">✅ Code Verified</span>
            <span className="auth-pill">⚡ Almost done</span>
          </div>
        </div>

        <div className="auth-orb auth-orb-1" />
        <div className="auth-orb auth-orb-2" />
        <div className="auth-orb auth-orb-3" />
      </div>

      <div className="auth-right">
        <div className="auth-card forgot-card-layout">
          <div className="auth-card-header">
            <div className="auth-card-logo">
              <img src={logoPng} alt="Easy World" />
            </div>

            <div>
              <h2 className="auth-card-title">Set new password</h2>
              <p className="auth-card-sub">Enter and confirm your new password</p>
            </div>
          </div>

          <div className="fp-illustration">
            <div className="fp-illustration-icon">
              <ShieldIcon />
            </div>

            <div className="fp-illustration-text">
              <div className="fp-illustration-title">Code verified</div>
              <div className="fp-illustration-sub">
                Setting a new password for {email || 'your account'}.
              </div>
            </div>
          </div>

          {error && (
            <div className="auth-error">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="auth-form" style={{ marginBottom: 0 }}>
            <div className="auth-field">
              <label className="auth-label">New Password</label>
              <div className="auth-input-wrap">
                <span className="auth-input-icon"><LockIcon /></span>
                <input
                  type="password"
                  className="auth-input"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); if (error) setError(''); }}
                  disabled={submitting}
                  required
                  autoFocus
                />
              </div>
            </div>

            <div className="auth-field">
              <label className="auth-label">Confirm Password</label>
              <div className="auth-input-wrap">
                <span className="auth-input-icon"><LockIcon /></span>
                <input
                  type="password"
                  className="auth-input"
                  placeholder="••••••••"
                  value={passwordConfirmation}
                  onChange={(e) => { setPasswordConfirmation(e.target.value); if (error) setError(''); }}
                  disabled={submitting}
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              className="auth-btn auth-btn-primary"
              disabled={submitting}
              style={{ marginTop: 8 }}
            >
              {submitting ? (
                <>
                  <span className="auth-btn-spinner" />&nbsp;Resetting…
                </>
              ) : (
                <>
                  Reset Password &nbsp;<ArrowIcon />
                </>
              )}
            </button>
          </form>

          <Link
            to="/login"
            className="auth-btn auth-btn-ghost"
            style={{ marginTop: 10, textDecoration: 'none', display: 'flex' }}
          >
            <BackIcon />&nbsp; Back to login
          </Link>
        </div>
      </div>
    </div>
  );
}